import { BILLING_ENABLED } from '@constants/features';
import type { TourDefinition, TourStep } from './types';

/**
 * Anchor token for a sidebar group, derived from its (untranslated) label so
 * the sidebar and the registry can never drift apart.
 */
export const sidebarAnchor = (label: string) =>
  `nav-${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')}`;

/**
 * Anchor token for a sidebar SUB-link, scoped by its parent group.
 *
 * Four sub-link labels are not unique across the menu — "Buat Aju", "Draft",
 * "Dashboard" and "Email Groups" each appear under two groups. An unscoped
 * token would resolve via querySelector to whichever renders first, which is
 * not the one the tour means.
 */
export const sidebarSubAnchor = (groupLabel: string, subLabel: string) =>
  `${sidebarAnchor(groupLabel)}--${sidebarAnchor(subLabel).replace(/^nav-/, '')}`;

/**
 * The PIB form's tabs, in the order the footer stepper walks them. Kept here
 * (not imported from the 6k-line edit page) so the registry test can assert
 * one tour per tab without dragging the whole page into a test process.
 */
export const PIB_TAB_IDS = [
  'header',
  'entitas',
  'dokumen',
  'pengangkut',
  'kemasan',
  'transaksi',
  'barang',
  'pungutan',
  'pernyataan',
] as const;

export type PibTabId = (typeof PIB_TAB_IDS)[number];

/** The PEB form's tabs. Note there is no `pungutan` — export levies (Bea
 *  Keluar, PPh 22, Pungutan Sawit) live inside the Transaksi tab. */
export const PEB_TAB_IDS = [
  'header',
  'entitas',
  'document',
  'pengangkut',
  'kemasan',
  'transaksi',
  'barang',
  'pernyataan',
] as const;

/* ------------------------------------------------------------------ *
 *  Spine — one tour per surface along the real PIB journey.
 *
 *  Pacing rule: a tour ENDS at the first point where the user has a real
 *  choice, or where the next thing is a different surface. Never chain a
 *  long sequence across pages — the destination's own tour takes over.
 * ------------------------------------------------------------------ */

const dashboardWelcome: TourDefinition = {
  id: 'dashboard-welcome',
  fork: true,
  steps: [
    { key: 'intro' },
    // Second, not last: knowing the tour can be replayed is what makes the rest
    // of it safe to skim.
    { key: 'help', anchor: 'tour-help-pill', side: 'bottom', align: 'end' },
    { key: 'stats', anchor: 'dashboard-stats', side: 'bottom' },
    { key: 'nav', anchor: 'nav-list', side: 'right', align: 'start', interactive: true },
    { key: 'search', anchor: 'nav-search', side: 'bottom', align: 'start', interactive: true },
    { key: 'kepabeanan', anchor: sidebarAnchor('Kepabeanan'), side: 'right', interactive: true },
    // Terminal: the real click navigates and /buat-aju's own tour takes over.
    {
      key: 'buatAju',
      anchor: sidebarSubAnchor('Kepabeanan', 'Buat Aju'),
      side: 'right',
      allowClick: true,
    },
  ],
};

/**
 * The full menu map. Deliberately NOT auto-started — it is 12 steps and would
 * bury a first-time user. `dashboard-welcome` ends by pointing at the "?" pill,
 * which offers this tour on every page.
 */
const sidebarMap: TourDefinition = {
  id: 'sidebar-map',
  steps: [
    { key: 'intro', anchor: 'nav-list', side: 'right', align: 'start', interactive: true },
    { key: 'mgmtDashboard', anchor: sidebarAnchor('Mgmt Dashboard'), side: 'right', interactive: true },
    { key: 'manifest', anchor: sidebarAnchor('Manifest'), side: 'right', interactive: true },
    { key: 'kepabeanan', anchor: sidebarAnchor('Kepabeanan'), side: 'right', interactive: true },
    { key: 'kuota', anchor: sidebarAnchor('Kuota Impor / Ekspor'), side: 'right', interactive: true },
    { key: 'cukai', anchor: sidebarAnchor('Cukai'), side: 'right', interactive: true },
    { key: 'customerApproval', anchor: sidebarAnchor('Customer Approval'), side: 'right', interactive: true },
    // Billing steps only exist when the billing area is shipped (VITE_BILLING_ENABLED).
    ...((BILLING_ENABLED
      ? [
          { key: 'creditTopup', anchor: sidebarAnchor('Credit Topup'), side: 'right', interactive: true },
          { key: 'langganan', anchor: sidebarAnchor('Langganan'), side: 'right', interactive: true },
        ]
      : []) as TourStep[]),
    { key: 'berita', anchor: sidebarAnchor('Berita'), side: 'right', interactive: true },
    { key: 'layananLainnya', anchor: sidebarAnchor('Layanan Lainnya'), side: 'right', interactive: true },
    { key: 'master', anchor: sidebarAnchor('Master'), side: 'right', interactive: true },
    { key: 'preferences', anchor: 'nav-language', side: 'bottom', align: 'end', interactive: true },
  ],
};

const buatAju: TourDefinition = {
  id: 'buat-aju',
  fork: true,
  steps: [
    { key: 'intro', anchor: 'buat-aju-grid', side: 'bottom', interactive: true },
    { key: 'export', anchor: 'buat-aju-export', side: 'bottom' },
    { key: 'ppftz', anchor: 'buat-aju-ppftz', side: 'bottom' },
    // Terminal: the real click navigates to the upload page, whose own tour takes over.
    { key: 'import', anchor: 'buat-aju-import', side: 'bottom', allowClick: true },
  ],
};

/**
 * The whole upload screen, splitting included.
 *
 * Longer than the other tours on purpose: getting the document split wrong is
 * how goods go missing from the PIB later, so the segment workflow is taught
 * stage by stage rather than summarised. The segment steps anchor controls that
 * only exist once a file is open in the preview; `skipMissingElement` drops
 * them when it is not, so the tour matches what is actually on screen.
 *
 * Side is chosen against the layout: this is a two-column screen, and a popover
 * on the same side as its anchor covers the very panel the step describes.
 */
const docPabeanUpload: TourDefinition = {
  id: 'doc-pabean-upload',
  fork: true,
  steps: [
    { key: 'intro', anchor: 'upload-header', side: 'bottom', align: 'start' },
    {
      key: 'dropzone',
      anchor: 'upload-dropzone',
      side: 'right',
      interactive: true,
      gate: { selector: '[data-tour="upload-file-row"]' },
    },
    {
      key: 'docType',
      anchor: 'upload-dropzone',
      side: 'right',
      interactive: true,
      gate: { selector: '[data-doc-type="none"]', absent: true },
    },
    // One PDF very often holds several documents; missing this is how barang
    // rows go missing later.
    { key: 'autoSplit', anchor: 'upload-auto-split', side: 'right', interactive: true },
    // Anchored on the FILE LIST, not the preview panel: this step asks the user
    // to open a file, and driver.js only frees the highlighted element — with
    // the spotlight on the preview the list stayed inert and the click was
    // impossible. Opening a file mounts the split controls, the step list
    // re-resolves, and the segment steps below become reachable.
    { key: 'preview', anchor: 'upload-file-list', side: 'right', align: 'start', interactive: true },
    { key: 'splitIntro', anchor: 'upload-segments', side: 'left', section: 'split' },
    {
      key: 'segmentAdd',
      anchor: 'upload-segment-add',
      side: 'left',
      interactive: true,
      gate: { selector: '[data-segment-row]' },
      // One click is enough to move on; leaving the user to decide whether a
      // second segment was expected is what made this step feel stuck.
      autoAdvance: true,
    },
    {
      key: 'segmentPages',
      anchor: 'upload-segment-thumbs',
      side: 'left',
      interactive: true,
      gate: { selector: '[data-segment-empty="true"]', absent: true },
    },
    { key: 'segmentRow', anchor: 'upload-segment-row', side: 'left', interactive: true },
    { key: 'segmentType', anchor: 'upload-segment-row', side: 'left', interactive: true },
    { key: 'segmentOriginal', anchor: 'upload-segment-original', side: 'left', interactive: true },
    { key: 'segmentExtract', anchor: 'upload-segment-extract', side: 'left', interactive: true },
    { key: 'reset', anchor: 'upload-reset', side: 'top' },
    // Terminal: Proses → Upload → Lanjut Review all live on this one button.
    { key: 'process', anchor: 'upload-primary-action', side: 'top', align: 'end', allowClick: true },
  ],
};

const docPabeanDraftList: TourDefinition = {
  id: 'doc-pabean-draft-list',
  fork: true,
  steps: [
    { key: 'search', anchor: 'draft-search', side: 'bottom', interactive: true },
    { key: 'intro', anchor: 'draft-table', side: 'top' },
    // Terminal: opening a draft navigates into the PIB form.
    { key: 'open', anchor: 'draft-table', side: 'top', allowClick: true },
  ],
};

const docPabeanList: TourDefinition = {
  id: 'doc-pabean-list',
  steps: [
    { key: 'intro', anchor: 'docpabean-table', side: 'top' },
    { key: 'credits', anchor: 'docpabean-credits', side: 'bottom' },
    { key: 'filters', anchor: 'docpabean-filters', side: 'bottom', interactive: true },
    { key: 'status', anchor: 'docpabean-table', side: 'top' },
    { key: 'report', anchor: 'docpabean-report', side: 'bottom' },
  ],
};

const pibEditOverview: TourDefinition = {
  id: 'pib-edit-overview',
  steps: [
    { key: 'intro' },
    { key: 'errors', anchor: 'pib-errors', side: 'bottom' },
    { key: 'toolbar', anchor: 'pib-toolbar', side: 'bottom' },
    { key: 'actions', anchor: 'pib-actions', side: 'bottom', align: 'end' },
    { key: 'footer', anchor: 'pib-footer-next', side: 'top', align: 'end' },
    // Hands off to the per-tab tours, which fire as each tab is first opened.
    { key: 'tabs', anchor: 'pib-tabs', side: 'bottom', align: 'start', interactive: true },
  ],
};

/* ------------------------------------------------------------------ *
 *  PIB tab tours — one per tab, fired the first time that tab is opened.
 *  Shape: what-is-this-tab, then a step per field group (interactive, so
 *  the user fills the real form while being taught), then the hand-off.
 * ------------------------------------------------------------------ */

const pibTabHeader: TourDefinition = {
  id: 'pib-tab-header',
  steps: [
    { key: 'intro', anchor: 'pib-header-form', side: 'top' },
    { key: 'pelabuhan', anchor: 'pib-header-pelabuhan', side: 'bottom', advanceOnChange: true },
    { key: 'kantorPabean', anchor: 'pib-header-kantor-pabean', side: 'bottom', advanceOnChange: true },
    { key: 'jenisPib', anchor: 'pib-header-pib', side: 'bottom', advanceOnChange: true },
    { key: 'jenisImpor', anchor: 'pib-header-jenis-impor', side: 'bottom', advanceOnChange: true },
    { key: 'caraBayar', anchor: 'pib-header-cara-bayar', side: 'bottom', advanceOnChange: true },
    { key: 'next', anchor: 'pib-footer-next', side: 'top', align: 'end', allowClick: true },
  ],
};

const pibTabEntitas: TourDefinition = {
  id: 'pib-tab-entitas',
  steps: [
    { key: 'intro', anchor: 'pib-entitas-list', side: 'top' },
    { key: 'importir', anchor: 'pib-entitas-importir', side: 'top', advanceOnChange: true },
    { key: 'pemilik', anchor: 'pib-entitas-pemilik', side: 'top', advanceOnChange: true },
    { key: 'pengirim', anchor: 'pib-entitas-pengirim', side: 'top', advanceOnChange: true },
    { key: 'penjual', anchor: 'pib-entitas-penjual', side: 'top', advanceOnChange: true },
    { key: 'ppjk', anchor: 'pib-entitas-ppjk', side: 'top', advanceOnChange: true },
    { key: 'pemusatan', anchor: 'pib-entitas-pemusatan', side: 'top' },
    { key: 'next', anchor: 'pib-footer-next', side: 'top', align: 'end', allowClick: true },
  ],
};

const pibTabDokumen: TourDefinition = {
  id: 'pib-tab-dokumen',
  steps: [
    { key: 'intro', anchor: 'pib-dokumen-list', side: 'top' },
    { key: 'wajib', anchor: 'pib-dokumen-list', side: 'top', interactive: true },
    { key: 'tambah', anchor: 'pib-dokumen-add', side: 'top', dismissOnClick: true },
  ],
};

const pibTabPengangkut: TourDefinition = {
  id: 'pib-tab-pengangkut',
  steps: [
    { key: 'intro', anchor: 'pib-pengangkut-form', side: 'top' },
    { key: 'sarana', anchor: 'pib-pengangkut-sarana', side: 'bottom', advanceOnChange: true },
    { key: 'bc11', anchor: 'pib-pengangkut-bc11', side: 'bottom', advanceOnChange: true },
    { key: 'tanggalTutup', anchor: 'pib-pengangkut-tanggal-tutup', side: 'bottom', advanceOnChange: true },
    { key: 'pos', anchor: 'pib-pengangkut-pos', side: 'bottom', advanceOnChange: true },
    { key: 'subpos', anchor: 'pib-pengangkut-subpos', side: 'bottom', advanceOnChange: true },
    { key: 'subsubpos', anchor: 'pib-pengangkut-subsubpos', side: 'bottom', advanceOnChange: true },
    { key: 'pelTujuan', anchor: 'pib-pengangkut-pel-tujuan', side: 'bottom', advanceOnChange: true },
    { key: 'next', anchor: 'pib-footer-next', side: 'top', align: 'end', allowClick: true },
  ],
};

const pibTabKemasan: TourDefinition = {
  id: 'pib-tab-kemasan',
  steps: [
    { key: 'intro', anchor: 'pib-kemasan-kemasan', side: 'top' },
    { key: 'jumlah', anchor: 'pib-kemasan-kemasan', side: 'top', interactive: true },
    { key: 'petiKemas', anchor: 'pib-kemasan-petikemas', side: 'top', interactive: true },
    { key: 'next', anchor: 'pib-footer-next', side: 'top', align: 'end', allowClick: true },
  ],
};

const pibTabTransaksi: TourDefinition = {
  id: 'pib-tab-transaksi',
  steps: [
    { key: 'intro', anchor: 'pib-transaksi-valuta', side: 'bottom' },
    { key: 'ndpbm', anchor: 'pib-transaksi-ndpbm', side: 'bottom', advanceOnChange: true },
    { key: 'jenisTransaksi', anchor: 'pib-transaksi-jenis-transaksi', side: 'bottom', advanceOnChange: true },
    { key: 'incoterm', anchor: 'pib-transaksi-incoterm', side: 'bottom', advanceOnChange: true },
    { key: 'hargaBarang', anchor: 'pib-transaksi-harga-barang', side: 'bottom', advanceOnChange: true },
    { key: 'asuransi', anchor: 'pib-transaksi-asuransi', side: 'bottom', advanceOnChange: true },
    { key: 'nilaiPabean', anchor: 'pib-transaksi-nilai-pabean', side: 'bottom' },
    { key: 'berat', anchor: 'pib-transaksi-berat-kotor', side: 'top', advanceOnChange: true },
    // Mid-tour modal opener: end the tour so the dialog is never trapped under the dim.
    { key: 'komponenBiaya', anchor: 'pib-transaksi-komponen-biaya', side: 'top', dismissOnClick: true },
  ],
};

const pibTabBarang: TourDefinition = {
  id: 'pib-tab-barang',
  steps: [
    { key: 'intro', anchor: 'pib-barang-table', side: 'top' },
    { key: 'baris', anchor: 'pib-barang-table', side: 'top', interactive: true },
    { key: 'excel', anchor: 'pib-toolbar', side: 'bottom' },
    { key: 'next', anchor: 'pib-footer-next', side: 'top', align: 'end', allowClick: true },
  ],
};

const pibTabPungutan: TourDefinition = {
  id: 'pib-tab-pungutan',
  steps: [
    { key: 'intro', anchor: 'pib-pungutan-table', side: 'top' },
    { key: 'jenis', anchor: 'pib-pungutan-table', side: 'top' },
    { key: 'generate', anchor: 'pib-actions', side: 'bottom', align: 'end' },
    { key: 'next', anchor: 'pib-footer-next', side: 'top', align: 'end', allowClick: true },
  ],
};

const pibTabPernyataan: TourDefinition = {
  id: 'pib-tab-pernyataan',
  steps: [
    { key: 'intro', anchor: 'pib-pernyataan-form', side: 'top' },
    { key: 'tanggungJawab', anchor: 'pib-pernyataan-form', side: 'top', advanceOnChange: true },
    { key: 'cetak', anchor: 'pib-actions', side: 'bottom', align: 'end' },
    { key: 'kirim', anchor: 'pib-actions', side: 'bottom', align: 'end' },
  ],
};

/* ------------------------------------------------------------------ *
 *  PEB (BC 3.0) — export. Its own path through the same upload screen.
 * ------------------------------------------------------------------ */

const docPabeanUploadExport: TourDefinition = {
  id: 'doc-pabean-upload-export',
  fork: true,
  steps: [
    { key: 'intro', anchor: 'upload-header', side: 'bottom', align: 'start' },
    {
      key: 'dropzone',
      anchor: 'upload-dropzone',
      side: 'right',
      interactive: true,
      gate: { selector: '[data-tour="upload-file-row"]' },
    },
    {
      key: 'docType',
      anchor: 'upload-dropzone',
      side: 'right',
      interactive: true,
      gate: { selector: '[data-doc-type="none"]', absent: true },
    },
    { key: 'autoSplit', anchor: 'upload-auto-split', side: 'right', interactive: true },
    { key: 'reset', anchor: 'upload-reset', side: 'top' },
    { key: 'process', anchor: 'upload-primary-action', side: 'top', align: 'end', allowClick: true },
  ],
};

/**
 * The PEB form. Steps walk the tab rail rather than each form's internals: the
 * rail is one component and stable, where the eight forms render through
 * fragments and portals. Per-tab PEB tours can follow the PIB pattern later.
 */
const exportForm: TourDefinition = {
  id: 'export-form',
  steps: [
    { key: 'intro' },
    { key: 'actions', anchor: 'peb-actions', side: 'bottom', align: 'end' },
    { key: 'submit', anchor: 'peb-submit', side: 'bottom', align: 'end' },
    { key: 'tabs', anchor: 'peb-tabs', side: 'bottom', align: 'start', interactive: true },
  ],
};

// Header is anchored field by field; a step that spotlights the whole panel
// tells the user nothing about which control it means.
const pebTabHeader: TourDefinition = {
  id: 'peb-tab-header',
  steps: [
    { key: 'intro', anchor: 'peb-header-pelabuhan', side: 'bottom' },
    { key: 'pelabuhan', anchor: 'peb-header-pelabuhan', side: 'bottom', advanceOnChange: true },
    { key: 'kantor', anchor: 'peb-header-kantor-asal', side: 'bottom', advanceOnChange: true },
    { key: 'kantorEkspor', anchor: 'peb-header-kantor-ekspor', side: 'bottom' },
    { key: 'jenisEkspor', anchor: 'peb-header-jenis-ekspor', side: 'bottom', advanceOnChange: true },
    { key: 'kategori', anchor: 'peb-header-kategori', side: 'bottom', advanceOnChange: true },
    { key: 'caraDagang', anchor: 'peb-header-cara-dagang', side: 'bottom', advanceOnChange: true },
    { key: 'caraBayar', anchor: 'peb-header-cara-bayar', side: 'bottom', advanceOnChange: true },
    { key: 'komoditi', anchor: 'peb-header-komoditi', side: 'bottom', advanceOnChange: true },
    { key: 'curah', anchor: 'peb-header-curah', side: 'top', advanceOnChange: true },
    { key: 'barangKiriman', anchor: 'peb-header-barang-kiriman', side: 'top', advanceOnChange: true },
    { key: 'next', anchor: 'peb-footer-next', side: 'top', align: 'end', allowClick: true },
  ],
};

/* PEB tab tours. Every step points at the control it describes: anchoring a run
 * of steps at the tab's <fieldset> spotlighted the whole panel each time, and
 * with side:'top' on a full-height element driver.js could not place the
 * popover at all and dropped it into a corner. */

const pebNext = {
  key: 'next',
  anchor: 'peb-footer-next',
  side: 'top' as const,
  align: 'end' as const,
  allowClick: true,
};

const pebTabEntitas: TourDefinition = {
  id: 'peb-tab-entitas',
  steps: [
    { key: 'intro', anchor: 'peb-entitas-npwp-eksportir', side: 'bottom' },
    { key: 'eksportir', anchor: 'peb-entitas-nama-eksportir', side: 'bottom', advanceOnChange: true },
    { key: 'alamatEksportir', anchor: 'peb-entitas-alamat-eksportir', side: 'bottom', advanceOnChange: true },
    { key: 'penerima', anchor: 'peb-entitas-nama-penerima', side: 'bottom', advanceOnChange: true },
    { key: 'negaraPenerima', anchor: 'peb-entitas-negara-penerima', side: 'bottom', advanceOnChange: true },
    { key: 'pembeli', anchor: 'peb-entitas-nama-pembeli', side: 'bottom', advanceOnChange: true },
    { key: 'negaraPembeli', anchor: 'peb-entitas-negara-pembeli', side: 'bottom', advanceOnChange: true },
    { key: 'ppjk', anchor: 'peb-entitas-nama-ppjk', side: 'bottom', advanceOnChange: true },
    { key: 'konsolidasi', anchor: 'peb-entitas-lokasi-konsolidasi', side: 'top', advanceOnChange: true },
    pebNext,
  ],
};

const pebTabDocument: TourDefinition = {
  id: 'peb-tab-document',
  steps: [
    { key: 'intro', anchor: 'peb-document-table', side: 'top' },
    { key: 'daftar', anchor: 'peb-document-table', side: 'top', interactive: true },
    pebNext,
  ],
};

const pebTabPengangkut: TourDefinition = {
  id: 'peb-tab-pengangkut',
  steps: [
    { key: 'intro', anchor: 'peb-pengangkut-pelabuhan-muat-asal', side: 'bottom' },
    { key: 'muatEkspor', anchor: 'peb-pengangkut-pelabuhan-muat-ekspor', side: 'bottom', advanceOnChange: true },
    { key: 'penimbunan', anchor: 'peb-pengangkut-tempat-penimbunan', side: 'bottom', advanceOnChange: true },
    { key: 'tanggal', anchor: 'peb-pengangkut-tanggal-perkiraan-ekspor', side: 'bottom', advanceOnChange: true },
    { key: 'sarana', anchor: 'peb-pengangkut-jenis-pengangkutan', side: 'bottom', advanceOnChange: true },
    { key: 'tujuan', anchor: 'peb-pengangkut-pelabuhan-tujuan', side: 'bottom', advanceOnChange: true },
    { key: 'negara', anchor: 'peb-pengangkut-negara-tujuan-ekspor', side: 'bottom', advanceOnChange: true },
    { key: 'pemeriksaan', anchor: 'peb-pengangkut-lokasi-pemeriksaan', side: 'top', advanceOnChange: true },
    pebNext,
  ],
};

const pebTabKemasan: TourDefinition = {
  id: 'peb-tab-kemasan',
  steps: [
    { key: 'intro', anchor: 'peb-kemasan-table', side: 'top' },
    { key: 'kemasan', anchor: 'peb-kemasan-table', side: 'top', interactive: true },
    pebNext,
  ],
};

const pebTabTransaksi: TourDefinition = {
  id: 'peb-tab-transaksi',
  steps: [
    { key: 'intro', anchor: 'peb-transaksi-valuta', side: 'bottom' },
    { key: 'ndpbm', anchor: 'peb-transaksi-ndpbm', side: 'bottom', advanceOnChange: true },
    { key: 'penyerahan', anchor: 'peb-transaksi-cara-penyerahan', side: 'bottom', advanceOnChange: true },
    { key: 'nilai', anchor: 'peb-transaksi-nilai-ekspor-incoterm-fob', side: 'bottom', advanceOnChange: true },
    { key: 'asuransi', anchor: 'peb-transaksi-asuransi', side: 'bottom', advanceOnChange: true },
    { key: 'maklon', anchor: 'peb-transaksi-nilai-maklon', side: 'bottom', advanceOnChange: true },
    { key: 'beratKotor', anchor: 'peb-transaksi-berat-kotor-kgm', side: 'top', advanceOnChange: true },
    { key: 'beratBersih', anchor: 'peb-transaksi-berat-bersih-kgm', side: 'top', advanceOnChange: true },
    pebNext,
  ],
};

const pebTabBarang: TourDefinition = {
  id: 'peb-tab-barang',
  steps: [
    { key: 'intro', anchor: 'peb-barang-table', side: 'top' },
    { key: 'baris', anchor: 'peb-barang-table', side: 'top', interactive: true },
    pebNext,
  ],
};

const pebTabPernyataan: TourDefinition = {
  id: 'peb-tab-pernyataan',
  steps: [
    { key: 'intro', anchor: 'peb-pernyataan-nama', side: 'bottom' },
    { key: 'jabatan', anchor: 'peb-pernyataan-jabatan', side: 'bottom', advanceOnChange: true },
    { key: 'tempat', anchor: 'peb-pernyataan-tempat', side: 'bottom', advanceOnChange: true },
    { key: 'tanggal', anchor: 'peb-pernyataan-tanggal', side: 'bottom', advanceOnChange: true },
    { key: 'submit', anchor: 'peb-submit', side: 'bottom', align: 'end' },
  ],
};

export const TOURS: TourDefinition[] = [
  dashboardWelcome,
  sidebarMap,
  buatAju,
  docPabeanUpload,
  docPabeanUploadExport,
  exportForm,
  pebTabHeader,
  pebTabEntitas,
  pebTabDocument,
  pebTabPengangkut,
  pebTabKemasan,
  pebTabTransaksi,
  pebTabBarang,
  pebTabPernyataan,
  docPabeanDraftList,
  docPabeanList,
  pibEditOverview,
  pibTabHeader,
  pibTabEntitas,
  pibTabDokumen,
  pibTabPengangkut,
  pibTabKemasan,
  pibTabTransaksi,
  pibTabBarang,
  pibTabPungutan,
  pibTabPernyataan,
];

const byId = new Map(TOURS.map((tour) => [tour.id, tour]));

export const tourById = (id: string | undefined): TourDefinition | undefined =>
  id === undefined ? undefined : byId.get(id);

interface RouteTour {
  tourId: string;
  /** Query parameters that must all match for the tour to apply. */
  params?: Record<string, string>;
}

/**
 * Exact pathnames first, then patterns for id-bearing routes. Order matters:
 * `/doc-pabean/draft` must not be swallowed by the `/doc-pabean/draft/:id/edit`
 * pattern, so exact matches win.
 */
const EXACT_ROUTE_TOURS: Record<string, RouteTour | RouteTour[]> = {
  '/dashboard': { tourId: 'dashboard-welcome' },
  '/buat-aju': { tourId: 'buat-aju' },
  '/doc-pabean': { tourId: 'doc-pabean-list' },
  // Export (BC 3.0) and PPFTZ share this path with a different component tree
  // and different rules. The tour is written for PIB, and pathname alone would
  // have fired it — teaching import document handling on a PEB screen.
  '/doc-pabean/upload': [
    { tourId: 'doc-pabean-upload', params: { type: 'import' } },
    { tourId: 'doc-pabean-upload-export', params: { type: 'export' } },
  ],
  '/export': { tourId: 'export-form' },
  '/doc-pabean/draft': { tourId: 'doc-pabean-draft-list' },
};

const PATTERN_ROUTE_TOURS: Array<{ pattern: RegExp; tourId: string }> = [
  { pattern: /^\/doc-pabean\/draft\/[^/]+\/edit\/?$/, tourId: 'pib-edit-overview' },
];

/**
 * The tour that auto-runs at a given location, if any. `search` matters where
 * one path serves several document types.
 */
export const tourForPathname = (pathname: string, search = ''): TourDefinition | undefined => {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const exact = EXACT_ROUTE_TOURS[normalized];
  if (exact) {
    const query = new URLSearchParams(search);
    const candidates = Array.isArray(exact) ? exact : [exact];
    const hit = candidates.find((candidate) =>
      candidate.params ? Object.entries(candidate.params).every(([key, value]) => query.get(key) === value) : true
    );
    return hit ? byId.get(hit.tourId) : undefined;
  }
  const matched = PATTERN_ROUTE_TOURS.find((entry) => entry.pattern.test(normalized));
  return matched ? byId.get(matched.tourId) : undefined;
};

/** The tour for a PIB form tab, fired the first time that tab is opened. */
export const tourForPibTab = (tabId: string): TourDefinition | undefined => byId.get(`pib-tab-${tabId}`);

/** The tour for a PEB form tab. */
export const tourForPebTab = (tabId: string): TourDefinition | undefined => byId.get(`peb-tab-${tabId}`);

/** Offered from the "?" pill on every page, never auto-started. */
export const GLOBAL_TOUR_ID = 'sidebar-map';
